import { MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  TextField,
} from '@mui/material';

import { Calendar, X } from 'lucide-react';

import {
  DATE_PRESETS,
  DateRangeMode,
  DateRangeValue,
  EMPTY_DATE_RANGE,
  formatDateRange,
  presetRange,
} from './dateRange';

interface DateRangeFilterProps {
  label: string;
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  /** `date` picks whole days, `datetime` also the time (audit logs). */
  mode?: DateRangeMode;
  minWidth?: number;
  fullWidth?: boolean;
}

const fieldSx = { '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.06)' } };
// Native date/time inputs: dark calendar popup and a visible calendar icon on the dark theme.
const nativeInputSx = { ...fieldSx, '& input': { colorScheme: 'dark', cursor: 'pointer' } };

/** Opens the browser's calendar from a click anywhere in the field, not only on the small icon. */
const openNativePicker = (event: MouseEvent<HTMLElement>) => {
  const input = (event.currentTarget as HTMLElement).querySelector('input');
  try {
    input?.showPicker?.();
  } catch {
    // Not allowed in this context (e.g. no user gesture): the field still works by typing.
  }
};

/**
 * One filter field for a from–to range: shows the chosen range (or "Any time"), opens a popover with
 * quick presets and the two bounds, and clears with the ✕.
 */
export const DateRangeFilter = ({
  label,
  value,
  onChange,
  mode = 'date',
  minWidth = 220,
  fullWidth,
}: DateRangeFilterProps) => {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const isEmpty = !value.from && !value.to;
  const display = value.preset
    ? t(`dateRange.${value.preset}`)
    : (formatDateRange(value, { from: t('dateRange.fromWord'), until: t('dateRange.untilWord') }) ??
      t('dateRange.anyTime'));
  const inputType = mode === 'date' ? 'date' : 'datetime-local';

  const choosePreset = (preset: (typeof DATE_PRESETS)[DateRangeMode][number] | null) => {
    onChange(preset ? presetRange(preset, mode) : EMPTY_DATE_RANGE);
    setAnchor(null);
  };

  return (
    <>
      <TextField
        size="small"
        label={label}
        value={display}
        fullWidth={fullWidth}
        onClick={(e) => setAnchor(e.currentTarget)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
            e.preventDefault();
            setAnchor(e.currentTarget);
          }
        }}
        InputProps={{
          readOnly: true,
          startAdornment: (
            <InputAdornment position="start">
              <Calendar size={16} />
            </InputAdornment>
          ),
          endAdornment: !isEmpty ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                aria-label={t('dateRange.clear')}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(EMPTY_DATE_RANGE);
                }}
              >
                <X size={14} />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        }}
        inputProps={{ 'aria-haspopup': 'dialog', style: { cursor: 'pointer' } }}
        sx={{
          minWidth,
          ...fieldSx,
          '& .MuiOutlinedInput-root': {
            ...fieldSx['& .MuiOutlinedInput-root'],
            cursor: 'pointer',
            ...(isEmpty ? {} : { '& fieldset': { borderColor: 'rgba(179, 236, 73, 0.6)' } }),
          },
        }}
      />
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            // The theme's paper is transparent; same surface as the app's menus.
            sx: {
              mt: 0.5,
              bgcolor: '#1a1a1a',
              backgroundImage: 'none',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              boxShadow: '0 12px 32px rgba(0, 0, 0, 0.6)',
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
          <List dense sx={{ minWidth: 170, py: 1, borderRight: { sm: '1px solid rgba(255, 255, 255, 0.08)' } }}>
            <ListItemButton selected={isEmpty} onClick={() => choosePreset(null)}>
              <ListItemText primary={t('dateRange.anyTime')} />
            </ListItemButton>
            {DATE_PRESETS[mode].map((preset) => (
              <ListItemButton key={preset} selected={value.preset === preset} onClick={() => choosePreset(preset)}>
                <ListItemText primary={t(`dateRange.${preset}`)} />
              </ListItemButton>
            ))}
          </List>
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 240 }}>
            <TextField
              size="small"
              type={inputType}
              label={t('dateRange.from')}
              value={value.from}
              onChange={(e) => onChange({ from: e.target.value, to: value.to })}
              onClick={openNativePicker}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: value.to || undefined }}
              sx={nativeInputSx}
            />
            <TextField
              size="small"
              type={inputType}
              label={t('dateRange.to')}
              value={value.to}
              onChange={(e) => onChange({ from: value.from, to: e.target.value })}
              onClick={openNativePicker}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: value.from || undefined }}
              sx={nativeInputSx}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <Button size="small" onClick={() => onChange(EMPTY_DATE_RANGE)} disabled={isEmpty}>
                {t('dateRange.clear')}
              </Button>
              <Button size="small" variant="outlined" onClick={() => setAnchor(null)} sx={{ borderRadius: '16px' }}>
                {t('dateRange.done')}
              </Button>
            </Box>
          </Box>
        </Box>
      </Popover>
    </>
  );
};
